// Shared type definitions, header-image catalog, and URL helper used across
// the webinar-builder sub-components. Keeping these in a single non-React
// module avoids circular imports between sibling section files.

export interface Speaker {
  id: string
  name: string
  title: string
  photoUrl: string
  // CSS object-position value, e.g. "50% 50%". Used to frame the head/face
  // inside the circular crop. Defaults to centered.
  objectPosition?: string
}

export interface WebinarSection {
  id: string
  type: 'header' | 'webinar_details' | 'speakers' | 'footer'
  content: any
  backgroundColor?: string
}

export interface TenantInfo {
  name: string
  logo_url?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
}

// Header image options for webinar banners — internal stock images (professional photos).
// IDs 100-104 are finance/trading banner images added in upstream feat: 9a05fcf.
export const headerImageOptions = [
  // Finance & Trading
  { id: 100, url: '/stock-images/market-charts.jpg', name: 'Trading Dashboard' },
  { id: 101, url: '/stock-images/financial-charts.jpg', name: 'Stock Ticker Board' },
  { id: 102, url: '/stock-images/financial-analysis.jpg', name: 'Market Analysis' },
  { id: 103, url: '/stock-images/trading-chart.jpg', name: 'Trading Chart' },
  { id: 104, url: '/stock-images/currency-bills.jpg', name: 'Currency' },
  // Business & Office
  { id: 1, url: '/stock-images/conference-room.jpg', name: 'Conference Room' },
  { id: 2, url: '/stock-images/virtual-meeting.jpg', name: 'Virtual Meeting' },
  { id: 3, url: '/stock-images/webinar-setup.jpg', name: 'Webinar Setup' },
  { id: 4, url: '/stock-images/digital-presentation.jpg', name: 'Digital Presentation' },
  { id: 5, url: '/stock-images/team-discussion.jpg', name: 'Team Discussion' },
  { id: 6, url: '/stock-images/business-collaboration.jpg', name: 'Business Collaboration' },
  { id: 7, url: '/stock-images/tech-conference.jpg', name: 'Tech Conference' },
  { id: 8, url: '/stock-images/modern-workspace.jpg', name: 'Modern Workspace' },
  // Aviation & Airports
  { id: 9, url: '/stock-images/airport-terminal.jpg', name: 'Airport Terminal' },
  { id: 10, url: '/stock-images/air-traffic-control.jpg', name: 'Air Traffic Control' },
  { id: 11, url: '/stock-images/airport-runway.jpg', name: 'Airport Runway' },
  // Police & Law Enforcement
  { id: 12, url: '/stock-images/police-vehicles.jpg', name: 'Police Vehicles' },
  { id: 13, url: '/stock-images/police-patrol.jpg', name: 'Police Patrol' },
  // Fire Department & Emergency
  { id: 14, url: '/stock-images/fire-department.jpg', name: 'Fire Department' },
  { id: 15, url: '/stock-images/firefighters-response.jpg', name: 'Firefighters Response' },
  { id: 16, url: '/stock-images/firefighters-action.jpg', name: 'Firefighters in Action' },
  { id: 17, url: '/stock-images/ambulance-emergency.jpg', name: 'Ambulance Emergency' },
  { id: 18, url: '/stock-images/emergency-vehicles.jpg', name: 'Emergency Vehicles' },
  // Healthcare & Hospital
  { id: 19, url: '/stock-images/hospital-corridor.jpg', name: 'Hospital Corridor' },
  { id: 20, url: '/stock-images/hospital-surgery.jpg', name: 'Hospital Surgery' },
  { id: 21, url: '/stock-images/trauma-center.jpg', name: 'Trauma Center' },
  // Military & Defense
  { id: 22, url: '/stock-images/military-formation.jpg', name: 'Military Formation' },
  { id: 23, url: '/stock-images/military-helicopter.jpg', name: 'Military Helicopter' },
  { id: 24, url: '/stock-images/military-parade.jpg', name: 'Military Parade' },
  // Technology & Infrastructure
  { id: 25, url: '/stock-images/server-room.jpg', name: 'Server Room' },
  { id: 26, url: '/stock-images/control-room.jpg', name: 'Control Room' },
  { id: 27, url: '/stock-images/network-infrastructure.jpg', name: 'Network Infrastructure' },
  // Communications
  { id: 28, url: '/stock-images/radio-tower.jpg', name: 'Radio Tower' },
  { id: 29, url: '/stock-images/communication-tower.jpg', name: 'Communication Tower' },
  { id: 30, url: '/stock-images/satellite-dish.jpg', name: 'Satellite Communications' },
  // Education
  { id: 31, url: '/stock-images/school-building.jpg', name: 'School Building' },
  { id: 32, url: '/stock-images/school-campus.jpg', name: 'School Campus' },
  // Maritime & Ports
  { id: 33, url: '/stock-images/port-harbor.jpg', name: 'Port Harbor' },
  { id: 34, url: '/stock-images/shipping-containers.jpg', name: 'Shipping Containers' },
  { id: 35, url: '/stock-images/naval-vessels.jpg', name: 'Naval Vessels' },
  { id: 36, url: '/stock-images/coast-guard-ship.jpg', name: 'Coast Guard Ship' },
  // Energy & Utilities
  { id: 37, url: '/stock-images/solar-energy.jpg', name: 'Solar Energy' },
  { id: 38, url: '/stock-images/wind-energy.jpg', name: 'Wind Energy' },
  // Security & Surveillance
  { id: 39, url: '/stock-images/security-camera.jpg', name: 'Security Camera' },
  { id: 40, url: '/stock-images/surveillance-system.jpg', name: 'Surveillance System' },
  // Transportation
  { id: 41, url: '/stock-images/highway-infrastructure.jpg', name: 'Highway Infrastructure' },
]

// Anchor a relative asset URL (e.g. "/stock-images/foo.jpg") to a public host.
// Two helpers, two call sites:
//   * `toAbsoluteUrl`     → the SENT-EMAIL `*ToHtml` serializers (mail clients
//                            can't resolve relative paths; always the support
//                            host, where own images are then CID-inlined).
//   * `toPreviewImageUrl` → the in-EDITOR React `<img>`/background previews
//                            (`/stock-images/*` → support host, but
//                            `/api/v1/image/*` uploads → the dashboard
//                            same-origin proxy, the only origin that reaches
//                            crm-api). A raw relative preview URL 404s to a
//                            broken placeholder on the SPA origin (and on the
//                            desktop app's `tauri://localhost`, which bundles no
//                            `/stock-images`) — that was the webinar-builder
//                            "images don't render" bug (2026-06-18).
// Re-exported from the single source of truth (`crm/lib/public-url.ts`, support
// host `https://support.xopnetworks.com`) so this builder shares the one host — it
// used to carry a duplicate hardcoded `https://crm.xopnetworks.com`, which drifted from
// the olive builder when the host moved. (DRY + host move 2026-06-06.)
export { toAbsoluteUrl, toPreviewImageUrl } from "@crm-src/lib/public-url"

// Accent palette used by the speakers section to color avatar fallbacks
// (initials when no photo) and speaker frames in the email HTML.
export const SPEAKER_ACCENT_PALETTE = ['#667eea', '#e53e3e', '#3182ce', '#38a169', '#805ad5', '#dd6b20']
